import React, { useState } from 'react';
import { OrgUnitTree } from './OrgUnitTree';
import { OrgUnitDetail } from './OrgUnitDetail';
import { OrgUnitForm } from './OrgUnitForm';
import { OrgUnitTypeManager } from './OrgUnitTypeManager';
import { PlusIcon, FilterIcon, SearchIcon, LayoutGridIcon, ListTreeIcon, SettingsIcon } from 'lucide-react';
interface OrgUnit {
  id: string;
  code: string;
  name: string;
  description?: string;
  orgUnitTypeId: string;
  orgUnitType: {
    id: string;
    name: string;
    code: string;
  };
  parentOrgUnitId?: string;
  path: string;
  depth: number;
  isActive: boolean;
  isArchived: boolean;
  isOrgRoot: boolean;
  currencyId?: string;
  glCodeSegment?: string;
  participatesInGLCoding: boolean;
  logoFileId?: string | null;
  logoUrl?: string | null;
  createdUtc: string;
  updatedUtc?: string;
  childOrgUnits: OrgUnit[];
  addresses: any[];
  contactIds: string[];
  registrations: any[];
}
export function OrgUnitManagement({
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState('tree');
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showTypeManager, setShowTypeManager] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'tree' | 'list'>('tree');
  // Sample data - in production this would come from API
  const [orgUnits] = useState<OrgUnit[]>([{
    id: 'org-001',
    code: 'GLOBAL',
    name: 'Global Technologies Inc.',
    description: 'Parent company and legal entity',
    orgUnitTypeId: 'type-001',
    orgUnitType: {
      id: 'type-001',
      name: 'Legal Entity',
      code: 'ENTITY'
    },
    path: '/GLOBAL',
    depth: 0,
    isActive: true,
    isArchived: false,
    isOrgRoot: true,
    currencyId: 'usd',
    participatesInGLCoding: true,
    glCodeSegment: '1000',
    logoFileId: 'file-001',
    logoUrl: 'https://ui-avatars.com/api/?name=Global+Technologies&background=4F46E5&color=fff&size=200&bold=true&length=2',
    createdUtc: '2023-01-01T00:00:00Z',
    childOrgUnits: [{
      id: 'org-002',
      code: 'NY-HQ',
      name: 'New York Headquarters',
      description: 'Main office location',
      orgUnitTypeId: 'type-002',
      orgUnitType: {
        id: 'type-002',
        name: 'Physical Site',
        code: 'SITE'
      },
      parentOrgUnitId: 'org-001',
      path: '/GLOBAL/NY-HQ',
      depth: 1,
      isActive: true,
      isArchived: false,
      isOrgRoot: false,
      participatesInGLCoding: true,
      glCodeSegment: '1100',
      logoFileId: null,
      logoUrl: null,
      createdUtc: '2023-01-15T00:00:00Z',
      childOrgUnits: [{
        id: 'org-005',
        code: 'NY-FIN',
        name: 'Finance Department',
        description: 'Finance and accounting team',
        orgUnitTypeId: 'type-003',
        orgUnitType: {
          id: 'type-003',
          name: 'Department',
          code: 'DEPT'
        },
        parentOrgUnitId: 'org-002',
        path: '/GLOBAL/NY-HQ/NY-FIN',
        depth: 2,
        isActive: true,
        isArchived: false,
        isOrgRoot: false,
        participatesInGLCoding: true,
        glCodeSegment: '1110',
        logoFileId: null,
        logoUrl: null,
        createdUtc: '2023-02-01T00:00:00Z',
        childOrgUnits: [],
        addresses: [],
        contactIds: [],
        registrations: []
      }],
      addresses: [{
        id: 'addr-001',
        type: 'Physical',
        street: '123 Broadway',
        city: 'New York',
        state: 'NY',
        postalCode: '10001',
        country: 'United States'
      }],
      contactIds: ['con-004'],
      registrations: []
    }, {
      id: 'org-003',
      code: 'SF-OFFICE',
      name: 'San Francisco Office',
      orgUnitTypeId: 'type-002',
      orgUnitType: {
        id: 'type-002',
        name: 'Physical Site',
        code: 'SITE'
      },
      parentOrgUnitId: 'org-001',
      path: '/GLOBAL/SF-OFFICE',
      depth: 1,
      isActive: true,
      isArchived: false,
      isOrgRoot: false,
      participatesInGLCoding: true,
      glCodeSegment: '1200',
      logoFileId: null,
      logoUrl: null,
      createdUtc: '2023-01-20T00:00:00Z',
      childOrgUnits: [],
      addresses: [],
      contactIds: [],
      registrations: []
    }],
    addresses: [{
      id: 'addr-002',
      type: 'Registered',
      street: '123 Broadway',
      city: 'New York',
      state: 'NY',
      postalCode: '10001',
      country: 'United States'
    }],
    contactIds: ['con-003'],
    registrations: [{
      id: 'reg-001',
      type: 'Tax ID',
      value: '12-3456789',
      validFrom: '2023-01-01'
    }]
  }, {
    id: 'org-004',
    code: 'EURO-OPS',
    name: 'European Operations Ltd',
    description: 'European subsidiary',
    orgUnitTypeId: 'type-001',
    orgUnitType: {
      id: 'type-001',
      name: 'Legal Entity',
      code: 'ENTITY'
    },
    path: '/EURO-OPS',
    depth: 0,
    isActive: true,
    isArchived: false,
    isOrgRoot: true,
    currencyId: 'eur',
    participatesInGLCoding: true,
    glCodeSegment: '2000',
    logoFileId: 'file-002',
    logoUrl: 'https://ui-avatars.com/api/?name=European+Operations&background=10B981&color=fff&size=200&bold=true&length=2',
    createdUtc: '2023-02-01T00:00:00Z',
    childOrgUnits: [],
    addresses: [],
    contactIds: [],
    registrations: []
  }, {
    id: 'org-006',
    code: 'APAC-PTY',
    name: 'Asia Pacific Technologies Pty Ltd',
    description: 'Australian subsidiary and legal entity',
    orgUnitTypeId: 'type-001',
    orgUnitType: {
      id: 'type-001',
      name: 'Legal Entity',
      code: 'ENTITY'
    },
    path: '/APAC-PTY',
    depth: 0,
    isActive: true,
    isArchived: false,
    isOrgRoot: true,
    currencyId: 'aud',
    participatesInGLCoding: true,
    glCodeSegment: '3000',
    logoFileId: 'file-003',
    logoUrl: 'https://ui-avatars.com/api/?name=Asia+Pacific+Technologies&background=F59E0B&color=fff&size=200&bold=true&length=2',
    createdUtc: '2023-03-01T00:00:00Z',
    childOrgUnits: [{
      id: 'org-007',
      code: 'SYD-OFFICE',
      name: 'Sydney Office',
      description: 'Main Australian office',
      orgUnitTypeId: 'type-002',
      orgUnitType: {
        id: 'type-002',
        name: 'Physical Site',
        code: 'SITE'
      },
      parentOrgUnitId: 'org-006',
      path: '/APAC-PTY/SYD-OFFICE',
      depth: 1,
      isActive: true,
      isArchived: false,
      isOrgRoot: false,
      participatesInGLCoding: true,
      glCodeSegment: '3100',
      logoFileId: null,
      logoUrl: null,
      createdUtc: '2023-03-15T00:00:00Z',
      childOrgUnits: [],
      addresses: [{
        id: 'addr-003',
        type: 'Physical',
        street: '100 George Street',
        city: 'Sydney',
        state: 'NSW',
        postalCode: '2000',
        country: 'Australia'
      }],
      contactIds: ['con-001'],
      registrations: []
    }],
    addresses: [{
      id: 'addr-004',
      type: 'Registered',
      street: '100 George Street',
      city: 'Sydney',
      state: 'NSW',
      postalCode: '2000',
      country: 'Australia'
    }],
    contactIds: ['con-002'],
    registrations: [{
      id: 'reg-002',
      type: 'ABN',
      value: '51 824 753 556',
      validFrom: '2023-03-01',
      validTo: null
    }, {
      id: 'reg-003',
      type: 'ACN',
      value: '824 753 556',
      validFrom: '2023-03-01',
      validTo: null
    }]
  }]);
  const handleAddOrgUnit = (parentId?: string) => {
    setSelectedOrgUnit(parentId ? orgUnits.find(ou => ou.id === parentId) || null : null);
    setShowForm(true);
  };
  const handleEditOrgUnit = (orgUnit: OrgUnit) => {
    setSelectedOrgUnit(orgUnit);
    setShowForm(true);
  };
  const handleViewOrgUnit = (orgUnit: OrgUnit) => {
    setSelectedOrgUnit(orgUnit);
    setActiveTab('detail');
  };
  return <div data-id="element-19849">
      <div className="mb-8" data-id="element-19850">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-19851">
          Organisation Unit Management
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-19852">
          Manage your organisational structure with flexible hierarchical
          relationships
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6" data-id="element-19853">
        <nav className="-mb-px flex space-x-8" data-id="element-19854">
          <button onClick={() => setActiveTab('tree')} className={`pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'tree' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-19855">
            <ListTreeIcon className="h-5 w-5 inline mr-2" data-id="element-19856" />
            Organisation Tree
          </button>
          {selectedOrgUnit && <button onClick={() => setActiveTab('detail')} className={`pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'detail' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-19857">
              Details
            </button>}
          <button onClick={() => {
          setActiveTab('types');
          setShowTypeManager(true);
        }} className={`pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'types' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-19858">
            <SettingsIcon className="h-5 w-5 inline mr-2" data-id="element-19859" />
            OrgUnit Types
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'tree' && <div data-id="element-19860">
          {/* Toolbar */}
          <div className="mb-6 flex justify-between items-center" data-id="element-19861">
            <div className="flex-1 max-w-md" data-id="element-19862">
              <div className="relative rounded-md shadow-sm" data-id="element-19863">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-19864">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-19865" />
                </div>
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search organisation units..." data-id="element-19866" />
              </div>
            </div>
            <div className="flex space-x-2" data-id="element-19867">
              <button onClick={() => handleAddOrgUnit()} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19868">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-19869" />
                Add Root OrgUnit
              </button>
            </div>
          </div>

          {/* Tree View */}
          <OrgUnitTree orgUnits={orgUnits} searchTerm={searchTerm} onSelect={handleViewOrgUnit} onEdit={handleEditOrgUnit} onAddChild={handleAddOrgUnit} locale={locale} data-id="element-19870" />
        </div>}

      {activeTab === 'detail' && selectedOrgUnit && <OrgUnitDetail orgUnit={selectedOrgUnit} onEdit={handleEditOrgUnit} onClose={() => {
      setSelectedOrgUnit(null);
      setActiveTab('tree');
    }} locale={locale} data-id="element-19871" />}

      {activeTab === 'types' && <OrgUnitTypeManager locale={locale} data-id="element-19872" />}

      {/* Form Modal */}
      {showForm && <OrgUnitForm orgUnit={selectedOrgUnit} onClose={() => {
      setShowForm(false);
      setSelectedOrgUnit(null);
    }} onSave={data => {
      console.log('Saving org unit:', data);
      setShowForm(false);
      setSelectedOrgUnit(null);
    }} locale={locale} data-id="element-19873" />}
    </div>;
}